import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../icons';
import CloverLogo from '../../components/ui/CloverLogo';
import { forgotPassword, verifyResetOtp, resetPassword } from '../../api/auth';

const authLightTheme = {
  pageBg: '#f8fafc',
  text: '#0f172a',
  muted: '#64748b',
  subtle: '#94a3b8',
  label: '#475569',
  inputBg: '#ffffff',
  inputBorder: '#e2e8f0',
  inputText: '#0f172a',
  inputFocusBorder: '#4f46e5',
  inputFocusRing: 'rgba(79,70,229,0.1)',
  divider: '#e2e8f0',
  errorBg: '#fee2e2',
  errorBorder: '#fca5a5',
  errorText: '#991b1b',
  surface: '#ffffff',
  border: '#e2e8f0',
  chipBorder: '#e2e8f0',
  chipBg: '#ffffff',
  chipText: '#0f172a',
  chipSelectedBg: '#f5f3ff',
  chipSelectedBorder: '#4f46e5',
  chipSelectedText: '#4f46e5',
};

const authDarkTheme = {
  pageBg: '#020617',
  text: '#f8fafc',
  muted: '#cbd5e1',
  subtle: '#94a3b8',
  label: '#cbd5e1',
  inputBg: '#0f172a',
  inputBorder: '#334155',
  inputText: '#f8fafc',
  inputFocusBorder: '#818cf8',
  inputFocusRing: 'rgba(129,140,248,0.14)',
  divider: '#334155',
  errorBg: 'rgba(127,29,29,0.34)',
  errorBorder: '#7f1d1d',
  errorText: '#fca5a5',
  surface: '#0f172a',
  border: '#334155',
  chipBorder: '#334155',
  chipBg: '#0f172a',
  chipText: '#e2e8f0',
  chipSelectedBg: 'rgba(79,70,229,0.14)',
  chipSelectedBorder: '#818cf8',
  chipSelectedText: '#e0e7ff',
};

function useDarkMode() {
  const [isDark, setIsDark] = useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));

  useEffect(() => {
    const root = document.documentElement;
    const syncTheme = () => setIsDark(root.classList.contains('dark'));
    syncTheme();
    const observer = new MutationObserver(syncTheme);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

const getAuthTheme = (isDark) => (isDark ? authDarkTheme : authLightTheme);

function AuthField({ label, type = "text", value, onChange, placeholder, note, icon, showToggle }) {
  const isDark = useDarkMode();
  const theme = getAuthTheme(isDark);
  const [show, setShow] = useState(false);
  const actualType = showToggle ? (show ? "text" : "password") : type;
  return (
    <div>
      {label && <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: theme.label, marginBottom: 6 }}>{label}</label>}
      <div style={{ position: "relative" }}>
        {icon && <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: theme.subtle, display: "flex" }}><Icon name={icon} size={15} color={theme.subtle} /></div>}
        <input type={actualType} value={value} onChange={onChange} placeholder={placeholder}
          style={{ width: "100%", padding: icon ? "11px 40px 11px 38px" : "11px 40px 11px 14px", border: `1.5px solid ${theme.inputBorder}`, borderRadius: 10, fontSize: 13, color: theme.inputText, background: theme.inputBg, outline: "none", transition: "border-color .15s, box-shadow .15s", boxSizing: "border-box", fontFamily: "inherit" }}
          onFocus={e => { e.target.style.borderColor = theme.inputFocusBorder; e.target.style.boxShadow = `0 0 0 3px ${theme.inputFocusRing}`; }}
          onBlur={e => { e.target.style.borderColor = theme.inputBorder; e.target.style.boxShadow = "none"; }}
        />
        {showToggle && <button type="button" onClick={() => setShow(s => !s)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: theme.subtle, display: "flex", padding: 0 }}><Icon name="eye" size={15} color={theme.subtle} /></button>}
      </div>
      {note && <p style={{ fontSize: 11, color: theme.subtle, marginTop: 4 }}>{note}</p>}
    </div>
  );
}

function AuthPanel() {
  const features = [
    { icon: "maintenance", label: "Maintenance Billing", desc: "Track dues, record payments, generate receipts" },
    { icon: "vendors", label: "Vendor & AMC Mgmt", desc: "Manage contracts with expiry alerts" },
    { icon: "bank", label: "Bank Accounts", desc: "Secure multi-account financial overview" },
    { icon: "audit", label: "Audit Log", desc: "Complete trail of every action taken" },
  ];
  const stats = [{ value: "2,400+", label: "Societies" }, { value: "₹18Cr+", label: "Processed" }, { value: "99.9%", label: "Uptime" }];
  return (
    <div className="hidden lg:flex" style={{ width: 420, flexShrink: 0, background: "linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)", position: "relative", overflow: "hidden", flexDirection: "column", padding: "48px 40px" }}>
      <div style={{ position: "absolute", top: -80, left: -80, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(79,70,229,0.22) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -60, right: -60, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.18) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)", backgroundSize: "28px 28px", pointerEvents: "none" }} />
      <div style={{ display: "flex", alignItems: "center", marginBottom: 48, position: "relative" }}>
        <CloverLogo width={180} height={49} />
      </div>
      <div style={{ position: "relative", marginBottom: 40 }}>
        <div style={{ fontSize: 28, fontWeight: 900, color: "#fff", lineHeight: 1.2, letterSpacing: "-0.02em", marginBottom: 12 }}>
          Everything your society needs,{" "}
          <span style={{ background: "linear-gradient(135deg,#818cf8,#06b6d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>in one place.</span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 40, position: "relative" }}>
        {features.map((f, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Icon name={f.icon} size={15} color="rgba(255,255,255,0.65)" strokeWidth={1.6} />
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.85)", marginBottom: 2 }}>{f.label}</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.5 }}>{f.desc}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ position: "relative", display: "flex", gap: 0, marginTop: "auto", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, overflow: "hidden" }}>
        {stats.map((s, i) => (
          <div key={i} style={{ flex: 1, padding: "14px 10px", textAlign: "center", borderRight: i < stats.length - 1 ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#fff", letterSpacing: "-0.02em" }}>{s.value}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", marginTop: 2, textTransform: "uppercase", letterSpacing: ".06em" }}>{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ForgotPasswordScreen({ onBack }) {
  const isDark = useDarkMode();
  const theme = getAuthTheme(isDark);
  const [step, setStep] = useState("contact");
  const [contact, setContact] = useState("");
  const [channel, setChannel] = useState("mail"); // mail or sms
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPass, setNewPass] = useState("");
  const [confPass, setConfPass] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const otpRefs = useRef(Array.from({ length: 6 }, () => null));

  const sendOtp = async () => {
    if (!contact) { setError("Please enter your email or phone number."); return; }
    setError(""); setLoading(true);
    try {
      await forgotPassword(contact, channel);
      setStep("otp");
    } catch (err) {
      const message = err.errors ? Object.values(err.errors).flat()[0] : (err.response?.data?.message || err.message);
      setError(message || "Failed to send OTP.");
    } finally {
      setLoading(false);
    }
  };
  const verifyOtp = async () => {
    const code = otp.join("");
    if (code.length < 6) { setError("Please enter the 6-digit code."); return; }
    setError(""); setLoading(true);
    try {
      const response = await verifyResetOtp(contact, code);
      setResetToken(response.reset_token);
      setStep("reset");
    } catch (err) {
      const message = err.errors ? Object.values(err.errors).flat()[0] : (err.response?.data?.message || err.message);
      setError(message || "Invalid OTP.");
    } finally {
      setLoading(false);
    }
  };
  const handleResetPassword = async () => {
    if (!newPass || newPass.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (newPass !== confPass) { setError("Passwords do not match."); return; }
    setError(""); setLoading(true);
    try {
      await resetPassword(resetToken, newPass, confPass);
      setStep("done");
    } catch (err) {
      const message = err.errors ? Object.values(err.errors).flat()[0] : (err.response?.data?.message || err.message);
      setError(message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };
  const handleOtpKey = (i, e) => {
    const val = e.target.value.replace(/\D/, "").slice(-1);
    const next = [...otp]; next[i] = val; setOtp(next);
    if (val && i < 5) otpRefs.current[i + 1]?.focus();
    if (!val && i > 0 && e.nativeEvent.inputType === "deleteContentBackward") otpRefs.current[i - 1]?.focus();
  };

  const stepMeta = {
    contact: { icon: "mail", title: "Forgot your password?", sub: "Enter your registered email or phone number and we'll send you a one-time code." },
    otp: { icon: "shield", title: "Check your messages", sub: `We sent a 6-digit code to ${contact || "you"}.` },
    reset: { icon: "lock", title: "Set a new password", sub: "Choose a strong password for your account." },
    done: { icon: "check", title: "Password updated!", sub: "Your password has been reset. You can now sign in." },
  };
  const meta = stepMeta[step];

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: theme.pageBg }}>
      <AuthPanel />
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", overflowY: "auto" }}>
        <div style={{ width: "100%", maxWidth: 400, animation: "fadeUp .4s ease forwards" }}>
          {step !== "done" && (
            <button onClick={onBack} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: theme.muted, fontWeight: 600, background: "none", border: "none", cursor: "pointer", marginBottom: 32, padding: 0, fontFamily: "inherit" }}
              onMouseEnter={e => e.currentTarget.style.color = theme.text}
              onMouseLeave={e => e.currentTarget.style.color = theme.muted}>
              <Icon name="chevron_right" size={14} color="currentColor" style={{ transform: "rotate(180deg)" }} />
              Back to sign in
            </button>
          )}
          <div style={{ width: 56, height: 56, borderRadius: 16, background: step === "done" ? "linear-gradient(135deg,#10b981,#059669)" : "linear-gradient(135deg,#4f46e5,#7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20, boxShadow: step === "done" ? "0 4px 16px rgba(16,185,129,0.35)" : "0 4px 16px rgba(79,70,229,0.35)" }}>
            <Icon name={meta.icon} size={24} color="white" strokeWidth={2} />
          </div>
          <div style={{ fontSize: 24, fontWeight: 900, color: theme.text, letterSpacing: "-0.02em", marginBottom: 6 }}>{meta.title}</div>
          <div style={{ fontSize: 13, color: theme.muted, marginBottom: 28, lineHeight: 1.6 }}>{meta.sub}</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 28 }}>
            {["contact", "otp", "reset"].map((s, i) => (
              <div key={i} style={{ height: 3, flex: 1, borderRadius: 99, background: ["contact", "otp", "reset", "done"].indexOf(step) >= i ? "linear-gradient(135deg,#4f46e5,#7c3aed)" : theme.divider, transition: "background .3s" }} />
            ))}
          </div>
          {error && (
            <div style={{ background: theme.errorBg, border: `1px solid ${theme.errorBorder}`, borderRadius: 10, padding: "10px 14px", fontSize: 12, color: theme.errorText, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="alert" size={13} color={theme.errorText} /> {error}
            </div>
          )}
          {step === "contact" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", gap: 8, background: theme.inputBg, padding: 4, borderRadius: 12, border: `1.5px solid ${theme.inputBorder}` }}>
                <button onClick={() => setChannel("mail")} style={{ flex: 1, padding: "8px", borderRadius: 8, border: "none", background: channel === "mail" ? (isDark ? "#334155" : "#f1f5f9") : "transparent", color: channel === "mail" ? theme.text : theme.muted, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all .2s" }}>Email</button>
                <button onClick={() => setChannel("sms")} style={{ flex: 1, padding: "8px", borderRadius: 8, border: "none", background: channel === "sms" ? (isDark ? "#334155" : "#f1f5f9") : "transparent", color: channel === "sms" ? theme.text : theme.muted, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all .2s" }}>SMS</button>
              </div>
              <AuthField label={channel === "mail" ? "Registered email address" : "Registered phone number"} type={channel === "mail" ? "email" : "tel"} icon={channel === "mail" ? "mail" : "phone"} value={contact} onChange={e => setContact(e.target.value)} placeholder={channel === "mail" ? "admin@yoursociety.in" : "9876543210"} />
              <button onClick={sendOtp} disabled={loading} style={{ width: "100%", padding: "12px", borderRadius: 11, border: "none", cursor: loading ? "not-allowed" : "pointer", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontSize: 14, fontWeight: 700, boxShadow: "0 4px 16px rgba(79,70,229,0.3)", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.75 : 1 }}>
                {loading ? <><span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> Sending code…</> : "Send OTP Code"}
              </button>
            </div>
          )}
          {step === "otp" && (
            <div>
              <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 20 }}>
                {otp.map((digit, i) => (
                  <input key={i} ref={el => otpRefs.current[i] = el} type="text" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpKey(i, e)}
                    style={{ width: "calc(16.6% - 5px)", maxWidth: 50, minWidth: 38, height: 52, textAlign: "center", fontSize: 20, fontWeight: 800, border: `2px solid ${digit ? '#4f46e5' : theme.inputBorder}`, borderRadius: 11, outline: "none", color: theme.inputText, background: digit ? (isDark ? 'rgba(79,70,229,0.14)' : '#f5f3ff') : theme.inputBg, transition: "all .15s", fontFamily: "inherit" }}
                    onFocus={e => { e.target.style.borderColor = theme.inputFocusBorder; e.target.style.boxShadow = `0 0 0 3px ${theme.inputFocusRing}`; }}
                    onBlur={e => { e.target.style.boxShadow = "none"; }} />
                ))}
              </div>
              <p style={{ textAlign: "center", fontSize: 12, color: "#94a3b8", marginBottom: 20 }}>
                Didn't receive it?{" "}
                <button onClick={sendOtp} style={{ color: isDark ? '#818cf8' : '#4f46e5', fontWeight: 600, background: "none", border: "none", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Resend code</button>
              </p>
              <button onClick={verifyOtp} disabled={loading} style={{ width: "100%", padding: "12px", borderRadius: 11, border: "none", cursor: loading ? "not-allowed" : "pointer", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.75 : 1, boxShadow: "0 4px 16px rgba(79,70,229,0.3)" }}>
                {loading ? <><span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> Verifying…</> : "Verify Code →"}
              </button>
            </div>
          )}
          {step === "reset" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <AuthField label="New password" icon="lock" showToggle value={newPass} onChange={e => setNewPass(e.target.value)} placeholder="Min 8 characters" />
              {newPass && (
                <div style={{ display: "flex", gap: 4 }}>
                  {[{ label: "Weak", met: newPass.length >= 1, color: "#ef4444" }, { label: "Fair", met: newPass.length >= 6, color: "#f59e0b" }, { label: "Strong", met: newPass.length >= 10 && /[A-Z]/.test(newPass) && /\d/.test(newPass), color: "#10b981" }].map((s, i) => (
                    <div key={i} style={{ flex: 1, height: 3, borderRadius: 99, background: s.met ? s.color : theme.divider, transition: "background .3s" }} />
                  ))}
                </div>
              )}
              <AuthField label="Confirm new password" icon="lock" showToggle value={confPass} onChange={e => setConfPass(e.target.value)} placeholder="Repeat your password" />
              <button onClick={handleResetPassword} disabled={loading} style={{ width: "100%", padding: "12px", borderRadius: 11, border: "none", cursor: loading ? "not-allowed" : "pointer", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.75 : 1, boxShadow: "0 4px 16px rgba(79,70,229,0.3)" }}>
                {loading ? <><span style={{ width: 16, height: 16, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} /> Updating password…</> : "Update Password"}
              </button>
            </div>
          )}
          {step === "done" && (
            <div style={{ textAlign: "center" }}>
              <div style={{ width: 80, height: 80, borderRadius: "50%", background: isDark ? 'rgba(16,185,129,0.18)' : '#d1fae5', display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
                <Icon name="check" size={36} color="#10b981" strokeWidth={2.5} />
              </div>
              <p style={{ fontSize: 13, color: theme.muted, marginBottom: 24, lineHeight: 1.7 }}>Your password has been updated successfully. Use your new password to sign in to your The Clover App dashboard.</p>
              <button onClick={onBack} style={{ width: "100%", padding: "12px", borderRadius: 11, border: "none", cursor: "pointer", background: "linear-gradient(135deg,#4f46e5,#7c3aed)", color: "#fff", fontSize: 14, fontWeight: 700, fontFamily: "inherit", boxShadow: "0 4px 16px rgba(79,70,229,0.3)" }}>Sign In Now →</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
